import { NvModal, NvTypography } from '@simwood/nv-react-components';
import React from 'react';
import * as styles from '../styles/changelogModal.module.scss';

type Block =
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'hr' };

function parseMarkdown(md: string): Block[] {
  const firstSeparator = md.indexOf('\n---\n');
  const content = firstSeparator !== -1 ? md.slice(firstSeparator + 5) : md;

  const blocks: Block[] = [];
  let pendingItems: string[] = [];

  const flushList = () => {
    if (pendingItems.length) {
      blocks.push({ type: 'ul', items: pendingItems });
      pendingItems = [];
    }
  };

  for (const line of content.split('\n')) {
    if (line.startsWith('## ')) {
      flushList();
      blocks.push({ type: 'h2', text: line.slice(3) });
    } else if (line.startsWith('### ')) {
      flushList();
      blocks.push({ type: 'h3', text: line.slice(4) });
    } else if (line === '---') {
      flushList();
      blocks.push({ type: 'hr' });
    } else if (line.startsWith('- ')) {
      pendingItems.push(line.slice(2));
    }
  }
  flushList();

  return blocks;
}

function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part,
  );
}

interface Props {
  content: string;
  onClose: () => void;
}

const ChangelogModal: React.FC<Props> = ({ content, onClose }) => (
  <NvModal.Dialog
    title="What's New"
    open={true}
    setOpen={(open) => {
      if (!open) onClose();
    }}
  >
    <div className={styles.content}>
      {parseMarkdown(content).map((block, i) => {
        switch (block.type) {
          case 'h2':
            return (
              <NvTypography key={i} variant='h5' className={styles.version}>
                {block.text}
              </NvTypography>
            );
          case 'h3':
            return (
              <NvTypography
                key={i}
                variant='caption'
                className={styles.section}
              >
                {block.text}
              </NvTypography>
            );
          case 'hr':
            return <hr key={i} className={styles.hr} />;
          case 'ul':
            return (
              <ul key={i} className={styles.list}>
                {block.items.map((item, j) => (
                  <li key={j} className={styles.listItem}>
                    {renderInline(item)}
                  </li>
                ))}
              </ul>
            );
        }
      })}
    </div>
  </NvModal.Dialog>
);

export default ChangelogModal;
